import { doc, getDoc } from 'firebase/firestore';

import { firestore } from 'services/firebase/client';
import { roundCurrency } from 'utils/currency';
import {
  DEFAULT_GROWLERS_CASHBACK_CONFIG,
  GROWLERS_CASHBACK_DOC_ID,
  type CashbackWallet,
  type GrowlersCashbackConfig,
} from 'utils/types/GrowlersCashback';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export const getGrowlersCashbackConfig =
  async (): Promise<GrowlersCashbackConfig> => {
    try {
      const ref = doc(firestore, 'site_config', GROWLERS_CASHBACK_DOC_ID);
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) {
        return DEFAULT_GROWLERS_CASHBACK_CONFIG;
      }
      const data = snapshot.data();
      return {
        enabled: Boolean(data.enabled),
        earnPercent: Number(data.earnPercent) || 0,
        redeemPercentPerOrder: Number(data.redeemPercentPerOrder) || 0,
      };
    } catch (error) {
      console.error('Erro ao buscar config de cashback de growlers:', error);
      return DEFAULT_GROWLERS_CASHBACK_CONFIG;
    }
  };

export const getCashbackWalletByEmail = async (
  email: string,
): Promise<CashbackWallet | null> => {
  try {
    const normalized = normalizeEmail(email);
    if (!normalized) {
      return null;
    }
    const ref = doc(firestore, 'cashback_wallets', normalized);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) {
      return null;
    }
    const data = snapshot.data();
    return {
      email: normalized,
      uid: String(data.uid || ''),
      balance: roundCurrency(Number(data.balance) || 0),
      cpf: data.cpf ? String(data.cpf) : undefined,
    };
  } catch (error) {
    console.error('Erro ao buscar carteira de cashback:', error);
    return null;
  }
};

export const calculateMaxCashbackRedeem = ({
  balance,
  redeemPercentPerOrder,
  merchandiseSubtotal,
}: {
  balance: number;
  redeemPercentPerOrder: number;
  merchandiseSubtotal: number;
}): number => {
  if (balance <= 0 || redeemPercentPerOrder <= 0 || merchandiseSubtotal <= 0) {
    return 0;
  }
  const fromBalance = roundCurrency((balance * redeemPercentPerOrder) / 100);
  return roundCurrency(
    Math.min(fromBalance, merchandiseSubtotal, balance),
  );
};

export const calculateCashbackEarnAmount = ({
  merchandiseSubtotal,
  earnPercent,
  enabled,
}: {
  merchandiseSubtotal: number;
  earnPercent: number;
  enabled: boolean;
}): number => {
  if (!enabled || earnPercent <= 0 || merchandiseSubtotal <= 0) {
    return 0;
  }
  return roundCurrency((merchandiseSubtotal * earnPercent) / 100);
};
